import React, { useState, useEffect, useCallback } from 'react';
import { useAuthStore, selectIsAdmin } from '../store/authStore';
import { reservationApi } from '../services/api';
import { FiCalendar, FiClock, FiTrash2, FiEye, FiAlertCircle, FiPlus } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { format, parseISO, differenceInMinutes } from 'date-fns';

function MyReservationsPage() {
  const { user, token } = useAuthStore();
  const isAdmin = useAuthStore(selectIsAdmin);
  const [reservations, setReservations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('upcoming'); // 'upcoming', 'past', 'all'
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Get user's reservations
  const fetchReservations = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      let response;
      
      if (isAdmin) {
        // Admin can see all reservations
        response = await reservationApi.getAll();
      } else {
        // Regular user sees only their reservations
        response = await reservationApi.getMyReservations();
      }
      
      const reservations = response.data.reservations || [];
      setReservations(reservations);
    } catch (err) {
      console.error('Error fetching reservations:', err);
      setError('Nepodařilo se načíst rezervace. Zkuste to prosím znovu.');
      toast.error('Chyba při načítání rezervací');
    } finally {
      setIsLoading(false);
    }
  }, [isAdmin, token]);

  useEffect(() => {
    fetchReservations();
  }, [fetchReservations]);

  // Filter reservations based on time range
  const filteredReservations = reservations.filter(reservation => {
    const now = new Date();
    const startDate = new Date(reservation.startTime);
    
    switch (timeRange) {
      case 'upcoming':
        return startDate >= now && !reservation.isCancelled;
      case 'past':
        return startDate < now || reservation.isCancelled;
      case 'all':
      default:
        return true;
    }
  });

  // Sort reservations by start time
  const sortedReservations = [...filteredReservations].sort((a, b) => {
    return new Date(a.startTime) - new Date(b.startTime);
  });

  // Cancel reservation
  const handleCancel = async (reservationId) => {
    try {
      // Confirm with user
      if (!window.confirm('Opravdu chcete zrušit tuto rezervaci?')) {
        return;
      }
      
      await reservationApi.delete(reservationId);
      toast.success('Rezervace zrušena');
      fetchReservations();
    } catch (err) {
      console.error('Error cancelling reservation:', err);
      const message = err.response?.data?.error?.message || 
                     'Nepodařilo se zrušit rezervaci';
      toast.error(message);
    }
  };

  // Calculate duration
  const getDuration = (startTime, endTime) => {
    const duration = differenceInMinutes(new Date(endTime), new Date(startTime));
    const hours = Math.floor(duration / 60);
    const minutes = duration % 60;
    
    if (hours === 0) {
      return `${minutes} min`;
    } else if (minutes === 0) {
      return `${hours} h`;
    } else {
      return `${hours} h ${minutes} min`;
    }
  };

  // Get reservation status
  const getStatus = (reservation) => {
    if (reservation.isCancelled) {
      return 'Zrušeno';
    }
    
    const now = new Date();
    const startDate = new Date(reservation.startTime);
    const endDate = new Date(reservation.endTime);
    
    if (startDate > now) {
      return 'Plánováno';
    } else if (endDate < now) {
      return 'Ukončeno';
    } else {
      return 'Průběh';
    }
  };

  // Get status color
  const getStatusColor = (reservation) => {
    if (reservation.isCancelled) {
      return 'bg-red-100 text-red-800';
    }
    
    const now = new Date();
    const startDate = new Date(reservation.startTime);
    const endDate = new Date(reservation.endTime);
    
    if (startDate > now) {
      return 'bg-blue-100 text-blue-800';
    } else if (endDate < now) {
      return 'bg-gray-100 text-gray-800';
    } else {
      return 'bg-green-100 text-green-800';
    }
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return format(date, 'dd.MM.yyyy HH:mm');
  };

  // Format short date
  const formatShortDate = (dateString) => {
    const date = new Date(dateString);
    return format(date, 'dd.MM. HH:mm');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Moje rezervace</h1>
          <p className="text-sm text-gray-600 mt-1">
            Přehled vašich rezervací zasedací místnosti
          </p>
        </div>
        
        <div className="flex flex-wrap gap-2 mt-4 lg:mt-0">
          {/* Time range filter */}
          <div className="flex bg-white rounded-lg shadow-sm border border-gray-200">
            <button
              onClick={() => setTimeRange('upcoming')}
              className={`px-4 py-2 text-sm font-medium rounded-l-lg transition-colors ${
                timeRange === 'upcoming' 
                  ? 'bg-blue-100 text-blue-700' 
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Nadcházející
            </button>
            <button
              onClick={() => setTimeRange('past')}
              className={`px-4 py-2 text-sm font-medium border-l border-r border-gray-200 transition-colors ${
                timeRange === 'past' 
                  ? 'bg-blue-100 text-blue-700' 
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Minulé
            </button>
            <button
              onClick={() => setTimeRange('all')}
              className={`px-4 py-2 text-sm font-medium rounded-r-lg transition-colors ${
                timeRange === 'all' 
                  ? 'bg-blue-100 text-blue-700' 
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Vše
            </button>
          </div>
          
          {/* New reservation button */}
          <button
            onClick={() => {
              // Redirect to calendar to create new reservation
              // Or we could open a modal directly
              window.location.href = '/';
            }}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg shadow-sm hover:bg-blue-700 transition-colors"
          >
            <FiPlus className="mr-2" />
            Nová rezervace
          </button>
        </div>
      </div>

      {/* Loading state */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <div className="loading-spinner"></div>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg flex items-center">
          <FiAlertCircle className="mr-2" />
          {error}
        </div>
      ) : sortedReservations.length === 0 ? (
        <div className="bg-white rounded-xl shadow-md p-12 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FiCalendar className="text-2xl text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Žádné rezervace
          </h3>
          <p className="text-gray-600 mb-4">
            {timeRange === 'upcoming' 
              ? 'Nemáte žádné nadcházející rezervace.'
              : timeRange === 'past' 
                ? 'Nemáte žádné minulé rezervace.'
                : 'Nemáte žádné rezervace.'}
          </p>
          <button
            onClick={() => {
              window.location.href = '/';
            }}
            className="flex items-center mx-auto px-4 py-2 bg-blue-600 text-white rounded-lg shadow-sm hover:bg-blue-700 transition-colors"
          >
            <FiPlus className="mr-2" />
            Vytvořit první rezervaci
          </button>
        </div>
      ) : (
        /* Reservations list */
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Název
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Datum a čas
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Trvání
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Stav
                  </th>
                  {isAdmin && (
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Uživatel
                    </th>
                  )}
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Akce
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedReservations.map((reservation) => (
                  <tr 
                    key={reservation.id} 
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="font-medium text-gray-900">{reservation.title}</div>
                      {reservation.description && isAdmin && (
                        <div className="text-sm text-gray-500 truncate max-w-xs">{reservation.description}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-gray-900">
                        {formatShortDate(reservation.startTime)}
                      </div>
                      <div className="text-sm text-gray-500">
                        {formatShortDate(reservation.endTime)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                      {getDuration(reservation.startTime, reservation.endTime)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(reservation)}`}>
                        {getStatus(reservation)}
                      </span>
                    </td>
                    {isAdmin && (
                      <td className="px-6 py-4 whitespace-nowrap text-gray-500">
                        {reservation.user?.firstName} {reservation.user?.lastName}
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                      <div className="flex justify-end space-x-2">
                        {!reservation.isCancelled && (
                          <>
                            <button
                              onClick={() => {
                                setSelectedReservation(reservation);
                                setShowDetailModal(true);
                              }}
                              className="text-blue-600 hover:text-blue-800 transition-colors"
                              title="Zobrazit detail"
                            >
                              <FiEye className="text-lg" />
                            </button>
                            {(isAdmin || reservation.userId === user.id) && (
                              <button
                                onClick={() => handleCancel(reservation.id)}
                                className="text-red-600 hover:text-red-800 transition-colors"
                                title="Zrušit rezervaci"
                              >
                                <FiTrash2 className="text-lg" />
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Mobile view */}
          <div className="lg:hidden">
            <div className="divide-y divide-gray-200">
              {sortedReservations.map((reservation) => (
                <div key={reservation.id} className="p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-medium text-gray-900">{reservation.title}</h3>
                      <p className="text-sm text-gray-500 mt-1">
                        {formatDate(reservation.startTime)} - {formatDate(reservation.endTime)}
                      </p>
                      <p className="text-sm text-gray-500">
                        Trvání: {getDuration(reservation.startTime, reservation.endTime)}
                      </p>
                      <span className={`inline-block mt-2 px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(reservation)}`}>
                        {getStatus(reservation)}
                      </span>
                    </div>
                    <div className="flex space-x-2">
                      {!reservation.isCancelled && (
                        <>
                          <button
                            onClick={() => {
                              setSelectedReservation(reservation);
                              setShowDetailModal(true);
                            }}
                            className="text-blue-600 hover:text-blue-800"
                            title="Zobrazit detail"
                          >
                            <FiEye />
                          </button>
                          {(isAdmin || reservation.userId === user.id) && (
                            <button
                              onClick={() => handleCancel(reservation.id)}
                              className="text-red-600 hover:text-red-800"
                              title="Zrušit"
                            >
                              <FiTrash2 />
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedReservation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Detail rezervace</h2>
              <button
                onClick={() => setShowDetailModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <FiX className="text-xl" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <h3 className="font-medium text-gray-900">{selectedReservation.title}</h3>
                {selectedReservation.description && isAdmin && (
                  <p className="text-gray-600 mt-2">{selectedReservation.description}</p>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-gray-500">Datum a čas</div>
                  <div className="font-medium text-gray-900">
                    {formatDate(selectedReservation.startTime)}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500">Konec</div>
                  <div className="font-medium text-gray-900">
                    {formatDate(selectedReservation.endTime)}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500">Trvání</div>
                  <div className="font-medium text-gray-900">
                    {getDuration(selectedReservation.startTime, selectedReservation.endTime)}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500">Stav</div>
                  <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${getStatusColor(selectedReservation)}`}>
                    {getStatus(selectedReservation)}
                  </span>
                </div>
                
                {isAdmin && selectedReservation.user && (
                  <div className="col-span-2">
                    <div className="text-gray-500">Uživatel</div>
                    <div className="font-medium text-gray-900">
                      {selectedReservation.user.firstName} {selectedReservation.user.lastName}
                    </div>
                  </div>
                )}
              </div>
              
              <div className="flex justify-end pt-4 border-t border-gray-200">
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg shadow-sm hover:bg-gray-50 transition-colors"
                >
                  Zavřít
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MyReservationsPage;
